import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { t } from '@/shared/lib/i18n'
import { getCsrfToken } from '@/shared/lib/utils'
import type { WebInstrumentsLotForm } from '@/shared/types/web'
import { LotForm } from '@/features/instrument-lots/LotForm'

type LotFormDialogBaseProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  instrumentId: number
  onSuccess?: (newLotDetailId?: number) => void
}

/** Create a brand-new lot (no existing lot or detail) */
type LotFormDialogCreateProps = LotFormDialogBaseProps & {
  lotId?: never
  cloneLotDetailId?: never
  lotDetailId?: never
}

/** Add detail (optionally cloning an existing one) to an existing lot */
type LotFormDialogAddDetailProps = LotFormDialogBaseProps & {
  lotId: number
  cloneLotDetailId?: number | null
  lotDetailId?: never
}

/** Edit existing lot detail */
type LotFormDialogEditProps = LotFormDialogBaseProps & {
  lotDetailId: number
  lotId?: never
  cloneLotDetailId?: never
}

export type LotFormDialogProps =
  | LotFormDialogCreateProps
  | LotFormDialogAddDetailProps
  | LotFormDialogEditProps

const buildFormDataUrl = (
  instrumentId: number,
  lotDetailId?: number | null,
  lotId?: number | null,
  cloneLotDetailId?: number | null,
): string => {
  if (lotDetailId != null) {
    return `/instruments/${instrumentId}/lots/${lotDetailId}/form_data`
  }

  const base = `/instruments/${instrumentId}/lots/form_data`
  const params = new URLSearchParams()
  if (lotId != null) params.set('lot_id', String(lotId))
  if (cloneLotDetailId != null) params.set('clone_lot_detail_id', String(cloneLotDetailId))
  const queryString = params.toString()
  return queryString ? `${base}?${queryString}` : base
}

export const LotFormDialog = ({
  open,
  onOpenChange,
  instrumentId,
  lotId,
  cloneLotDetailId,
  lotDetailId,
  onSuccess,
}: LotFormDialogProps) => {
  const dialogTitle =
    lotDetailId != null
      ? t('instruments.lotsForm.editTitle')
      : t('instruments.lotsForm.addDetailTitle')

  const handleSuccess = (newLotDetailId?: number) => {
    onOpenChange(false)
    onSuccess?.(newLotDetailId)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1rem)] max-w-none flex-col gap-0 overflow-hidden sm:max-w-[min(900px,calc(100vw-2rem))]">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {open && (
            <LotFormDialogContent
              instrumentId={instrumentId}
              lotId={lotId}
              lotDetailId={lotDetailId}
              cloneLotDetailId={cloneLotDetailId}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

type FormDataState =
  | { status: 'loading' }
  | { status: 'ready'; formData: WebInstrumentsLotForm }
  | { status: 'error'; message: string }

type LotFormDialogContentProps = {
  instrumentId: number
  lotId?: number | null
  lotDetailId?: number | null
  cloneLotDetailId?: number | null
  onSuccess: (newLotDetailId?: number) => void
  onCancel: () => void
}

const LotFormDialogContent = ({
  instrumentId,
  lotId,
  lotDetailId,
  cloneLotDetailId,
  onSuccess,
  onCancel,
}: LotFormDialogContentProps) => {
  const [formDataState, setFormDataState] = useState<FormDataState>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    const url = buildFormDataUrl(instrumentId, lotDetailId, lotId, cloneLotDetailId)

    fetch(url, {
      credentials: 'same-origin',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json() as Promise<{ form_data: WebInstrumentsLotForm }>
      })
      .then((data) => setFormDataState({ status: 'ready', formData: data.form_data }))
      .catch((error) => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        setFormDataState({ status: 'error', message: t('instruments.lotsForm.dialog.loadError') })
      })

    return () => controller.abort()
  }, [instrumentId, lotDetailId, lotId, cloneLotDetailId])

  if (formDataState.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">{t('instruments.lotsForm.dialog.loading')}</p>
      </div>
    )
  }

  if (formDataState.status === 'error') {
    return (
      <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">{formDataState.message}</p>
      </div>
    )
  }

  return (
    <LotForm
      instrumentId={instrumentId}
      formData={formDataState.formData}
      fromModal
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  )
}
